// ContactLinkContext.js - Complete Fixed Version
import React, { createContext, useState, useEffect, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getContactLinksByCard, updateContactLink as apiUpdateContactLink, deleteContactLink as apiDeleteContactLink, createContactLink } from '../apis/contactLinkApi';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';

const ContactLinkContext = createContext();

export function ContactLinkProvider({ children }) {
  const { getCurrentUserId, validateUserAccess, handleUnauthorizedAccess, isSignupFlow, authState } = useAuth();
  const { addError, handleApiCall } = useError();

  // Support multiple cards with individual contact links
  // Structure: { cardId: [contactLinks], cardId2: [contactLinks], ... }
  const [contactLinksByCard, setContactLinksByCard] = useState({});
  const [currentCardId, setCurrentCardId] = useState(null);
  const [contactLinkError, setContactLinkError] = useState(null);
  const [fetchedCards, setFetchedCards] = useState(new Set());
  const [contactLinkState, setContactLinkState] = useState({
    userId: '',
    cardId: '',
    type: '',
    details: {
      linkUrl: '',
      linkTitle: '',
      additionalDetails: {},
      formattedLink: '',
    },
    isHighlighted: false,
  });

  const [contactLinkUUID, setContactLinkUUID] = useState('');

  // ✅ ADDED: Request deduplication to prevent multiple simultaneous requests
  const [pendingContactLinkRequests, setPendingContactLinkRequests] = useState(new Map());

  // ✅ REMOVED: Rate limiting protection - unnecessary for small application

  // ✅ Enhanced debugging for the contactLinks getter
  const contactLinks = React.useMemo(() => {
    const result = currentCardId ? (contactLinksByCard[currentCardId] || []) : [];

    // 🔍 DEBUG: Log every access to contactLinks
    // console.log('🔍 CONTACT_LINKS_ACCESS:', {
    //   currentCardId,
    //   hasCardId: !!currentCardId,
    //   cardExists: !!contactLinksByCard[currentCardId],
    //   resultLength: result.length,
    //   allCards: Object.keys(contactLinksByCard),
    //   fullState: contactLinksByCard,
    //   timestamp: new Date().toISOString()
    // });

    return result;
  }, [currentCardId, contactLinksByCard]);

  // Set current card ID
  const setCurrentCard = (cardId) => {
    // console.log('🔍 ContactLinkContext: Setting current card:', cardId);
    setCurrentCardId(cardId);
  };

  // ✅ NEW: Add method to force refetch a specific card
  const forceRefetchCard = (cardId) => {
    console.log('🔍 ContactLinkContext: Force refetching card:', cardId);

    // ✅ ADDED: Debouncing to prevent rapid successive calls
    if (pendingContactLinkRequests.has(cardId)) {
      console.log('🔍 ContactLinkContext: Force refetch already in progress for cardId:', cardId);
      return;
    }

    setFetchedCards(prev => {
      const newSet = new Set(prev);
      newSet.delete(cardId);
      return newSet;
    });

    // Then fetch the card
    fetchContactLinksForCard(cardId, true);
  };

  // ✅ NEW: Add method to invalidate all cached cards
  const invalidateAllCache = () => {
    console.log('🔍 ContactLinkContext: Invalidating all cached cards');
    setFetchedCards(new Set());
  };

  // ✅ CONTEXT-ONLY: Check if we're in signup flow
  const isInSignupFlow = () => {
    // Check if we're in an active signup process
    const signupInProgress = localStorage.getItem('signupInProgress') === 'true';

    // Check if we're using a temporary card ID (signup or new card creation)
    const isUsingTempCard = currentCardId === 'tempCardId';

    // Check if we're authenticated (if not authenticated, we might be in signup)
    const isAuthenticated = authState?.isAuthenticated;

    // Check if the current cardId is a UUID (new card creation flow)
    const isCardCreationFlow = currentCardId && currentCardId.includes('-') && currentCardId.length >= 32;

    console.log('🔍 ContactLinkContext: isInSignupFlow check:', {
      signupInProgress,
      isUsingTempCard,
      isAuthenticated,
      isCardCreationFlow,
      currentCardId
    });

    // Return true for signup flow OR temp cards OR card creation flow
    return signupInProgress || isUsingTempCard || isCardCreationFlow;
  };

  // Only load UUID from localStorage if user is authenticated or in signup flow
  useEffect(() => {
    const isSignupFlowActive = isInSignupFlow();

    if (authState?.isAuthenticated || isSignupFlowActive) {
      // Check if there's already a UUID for this session or card
      if (!contactLinkUUID) {
        const existingUUID = localStorage.getItem('contactLinkUUID');
        if (existingUUID) {
          setContactLinkUUID(existingUUID);
        } else {
          const generatedUUID = uuidv4();
          setContactLinkUUID(generatedUUID);
          localStorage.setItem('contactLinkUUID', generatedUUID);
        }
      }
    } else {
      // Clear UUID if not authenticated and not in signup flow
      setContactLinkUUID('');
    }
  }, [contactLinkUUID, authState?.isAuthenticated]);

  // ✅ SIMPLIFIED: Flow detection for context-only approach
  const detectFlowType = (cardId, userId) => {
    const isSignupFlowActive = isInSignupFlow();
    const hasUser = !!userId;
    const isTempCard = cardId === 'tempCardId';

    // ✅ FIXED: Check if this is a card creation flow (UUID format with hyphens)
    const isCardCreationFlow = cardId && cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

    // ✅ FIXED: Check if this is a permanent MongoDB ObjectId (24 characters, hex, no hyphens)
    const isPermanentCard = cardId && cardId.length === 24 && /^[0-9a-fA-F]{24}$/.test(cardId);

    console.log('🔍 ContactLinkContext: Flow detection:', {
      cardId,
      hasUser,
      isTempCard,
      isSignupFlowActive,
      isCardCreationFlow,
      isPermanentCard,
      cardIdLength: cardId?.length || 0,
      cardIdFormat: cardId ? `${cardId.substring(0, 8)}...${cardId.substring(cardId.length - 8)}` : 'none'
    });

    // ✅ CONTEXT_ONLY: Only during signup, temp cards, or card creation
    if (isSignupFlowActive || isTempCard || isCardCreationFlow) {
      return 'CONTEXT_ONLY';
    }
    // ✅ DATABASE_SYNC: For permanent cards (MongoDB ObjectIds) or authenticated users
    else if (isPermanentCard || (hasUser && !isTempCard && !isCardCreationFlow)) {
      return 'DATABASE_SYNC';
    }
    // ✅ DEFAULT: Use database for all other cases
    else {
      return 'DATABASE_SYNC';
    }
  };

  // Enhanced error handling for contact link operations
  const handleContactLinkError = (error) => {
    console.error('🔍 ContactLinkContext: Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      stack: error.stack
    });

    // Skip error handling during signup flow for expected errors
    if (isInSignupFlow()) {
      // Skip authentication errors during signup
      if (error.message?.includes('Authentication required')) {
        console.log('🚀 ContactLinkContext: Suppressing authentication error during signup flow');
        return;
      }

      // Skip session expired errors during signup
      if (error.message?.includes('session has expired')) {
        console.log('🚀 ContactLinkContext: Suppressing session expired error during signup flow');
        return;
      }

      // Skip permission errors during signup
      if (error.message?.includes('permission')) {
        console.log('🚀 ContactLinkContext: Suppressing permission error during signup flow');
        return;
      }
    }

    // Add error to global error context for non-signup flows
    addError(error, {
      source: 'contact-link',
      type: 'contact-link-operation',
      retryable: true,
      autoDismiss: false
    });

    if (error.response?.status === 401) {
      setContactLinkError('Your session has expired. Please log in again');
      setTimeout(() => {
        window.location.href = '/sign-in';
      }, 2000);
      return;
    }

    if (error.response?.status === 403) {
      setContactLinkError('You do not have permission to modify this contact link');
      handleUnauthorizedAccess();
      return;
    }

    if (error.message?.includes('permission')) {
      setContactLinkError('You do not have permission to modify this contact link');
      return;
    }

    if (error.message?.includes('session')) {
      setContactLinkError('Your session has expired. Please log in again');
      setTimeout(() => {
        window.location.href = '/sign-in';
      }, 2000);
      return;
    }

    if (error.message?.includes('contact link not found') || error.message?.includes('Contact link not found')) {
      setContactLinkError('Contact link not found');
      return;
    }

    // Generic error handling
    setContactLinkError(error.message || 'An error occurred while updating the contact link');
  };

  // Clear error after timeout
  useEffect(() => {
    if (contactLinkError) {
      const timer = setTimeout(() => {
        setContactLinkError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [contactLinkError]);

  // ✅ Enhanced debugging for setContactLinksForCard
  const setContactLinksForCard = (cardId, newContactLinks) => {
    try {
      console.log('🔍 SET_CONTACT_LINKS_FOR_CARD_START:', {
        cardId,
        newContactLinksLength: Array.isArray(newContactLinks) ? newContactLinks.length : 'NOT_ARRAY',
        newContactLinks,
        beforeState: contactLinksByCard,
        timestamp: new Date().toISOString()
      });

      setContactLinkError(null);

      const safeNewContactLinks = Array.isArray(newContactLinks) ? newContactLinks : [];
      const currentUserId = getCurrentUserId();
      const flowType = detectFlowType(cardId, currentUserId);

      // ✅ FRONTEND-ONLY FIX: Process links to extract isVisible from additionalDetails
      const processedLinks = safeNewContactLinks.map(link => {
        // Extract isVisible from additionalDetails if it exists there
        const isVisibleFromDetails = link.details?.additionalDetails?.isVisible;

        // Use isVisible from additionalDetails, then root level, then default to true
        const isVisible = isVisibleFromDetails !== undefined
          ? isVisibleFromDetails
          : (link.isVisible !== undefined ? link.isVisible : true);

        console.log('🔍 Processing link visibility:', {
          linkId: link._id?.substring(0, 8) + '...',
          type: link.type,
          isVisibleFromDetails,
          isVisibleFromRoot: link.isVisible,
          finalIsVisible: isVisible
        });

        return {
          ...link,
          isVisible // Ensure isVisible is at root level for frontend use
        };
      });

      setContactLinksByCard(prev => {
        const newState = {
          ...prev,
          [cardId]: processedLinks
        };

        console.log('🔍 SET_CONTACT_LINKS_STATE_UPDATE:', {
          cardId,
          flowType,
          linksCount: processedLinks.length,
          linksWithVisibility: processedLinks.map(l => ({
            type: l.type,
            isVisible: l.isVisible,
            hasVisibilityInDetails: !!l.details?.additionalDetails?.isVisible
          })),
          previousState: prev,
          newState: newState,
          timestamp: new Date().toISOString()
        });

        return newState;
      });

      console.log('🔍 SET_CONTACT_LINKS_FOR_CARD_COMPLETED:', {
        cardId,
        count: processedLinks.length,
        visibleCount: processedLinks.filter(l => l.isVisible !== false).length
      });

    } catch (error) {
      console.error('🔍 SET_CONTACT_LINKS_ERROR:', error);
      handleContactLinkError(error);
    }
  };

  // ✅ MODIFIED: Enhanced fetch method with force parameter
  const fetchContactLinksForCard = async (cardId, force = false) => {
    try {
      setContactLinkError(null);
      if (!cardId) {
        console.warn('🔍 ContactLinkContext: No cardId provided for fetch');
        return;
      }

      // ✅ ADDED: Request deduplication to prevent multiple simultaneous requests
      if (pendingContactLinkRequests.has(cardId)) {
        console.log('🔍 ContactLinkContext: Request already in progress for cardId:', cardId);
        return await pendingContactLinkRequests.get(cardId);
      }

      // ✅ FIXED: Allow force refetch even if already fetched
      if (!force && fetchedCards.has(cardId)) {
        console.log('🔍 ContactLinkContext: Already fetched contact links for cardId (use force=true to refetch):', cardId);
        return;
      }

      const currentUserId = getCurrentUserId();
      const flowType = detectFlowType(cardId, currentUserId);

      console.log('🔍 ContactLinkContext: Fetching contact links:', {
        cardId,
        flowType,
        hasUser: !!currentUserId,
        force
      });

      if (flowType === 'CONTEXT_ONLY') {
        // ✅ SIGNUP FLOW: No backend calls, use empty array
        console.log('🔍 ContactLinkContext: CONTEXT_ONLY - no backend fetch, initializing empty');
        setContactLinksByCard(prev => ({
          ...prev,
          [cardId]: prev[cardId] || [] // Keep existing context data or initialize empty
        }));
        setFetchedCards(prev => new Set(prev).add(cardId));
        return;
      }

      // ✅ NORMAL FLOW: Fetch from database
      setFetchedCards(prev => new Set(prev).add(cardId));

      // Create a promise for this request
      const requestPromise = (async () => {
        console.log('🔍 ContactLinkContext: DATABASE_SYNC - fetching from backend');
        const response = await handleApiCall(
          () => getContactLinksByCard(cardId),
          {
            source: 'contact-link-fetch',
            retryable: true,
            autoDismiss: true
          }
        );

        if (response.success && Array.isArray(response.contactLinks)) {
          console.log('🔍 ContactLinkContext: Successfully fetched contact links:', {
            cardId,
            count: response.contactLinks.length,
            flowType,
            force
          });

          // ✅ Log video content for debugging
          const linksWithVideos = response.contactLinks.filter(link =>
            link.type === 'Products & Services' &&
            link.details?.videos &&
            Array.isArray(link.details.videos) &&
            link.details.videos.length > 0
          );

          console.log('🔍 ContactLinkContext: Products & Services links with videos:', {
            cardId,
            videoLinksCount: linksWithVideos.length,
            videoLinks: linksWithVideos.map(link => ({
              id: link._id,
              productTitle: link.details?.productTitle,
              videosCount: link.details?.videos?.length,
              videos: link.details?.videos
            }))
          });

          setContactLinksForCard(cardId, response.contactLinks);
        } else {
          console.log('🔍 ContactLinkContext: No contact links found for:', cardId);
          setContactLinksForCard(cardId, []);
        }
      })();

      // Store the promise in pending requests
      setPendingContactLinkRequests(prev => new Map(prev).set(cardId, requestPromise));

      try {
        const result = await requestPromise;
        return result;
      } finally {
        // Clean up the pending request
        setPendingContactLinkRequests(prev => {
          const newMap = new Map(prev);
          newMap.delete(cardId);
          return newMap;
        });
      }
    } catch (error) {
      console.error('🔍 ContactLinkContext: Error fetching contact links:', error);
      handleContactLinkError(error);
    }
  };

  // Enhanced contact link access validation
  const validateContactLinkAccess = (linkId, userId = getCurrentUserId()) => {
    // Skip validation during signup
    if (isInSignupFlow()) {
      return true;
    }

    const link = contactLinks.find(link => link._id === linkId || link.id === linkId);
    if (!link) {
      throw new Error('Contact link not found');
    }

    if (link.userId && link.userId !== userId) {
      throw new Error('You do not have permission to access this contact link');
    }

    return true;
  };

  // Check if user owns the contact link
  const isContactLinkOwner = (linkId, userId = getCurrentUserId()) => {
    // During signup, allow ownership of all links
    if (isInSignupFlow()) {
      return true;
    }

    const link = contactLinks.find(link => link._id === linkId || link.id === linkId);
    return link && link.userId === userId;
  };

  // Get contact links owned by current user
  const getUserContactLinks = (userId = getCurrentUserId()) => {
    return contactLinks.filter(link => link.userId === userId);
  };

  // Helper function to find link by ID (checks both _id and id)
  const findLinkById = (linkId, cardId) => {
    const links = contactLinksByCard[cardId] || [];
    return links.find(link => link._id === linkId || link.id === linkId);
  };

  // Helper function to update link in state
  const updateLinkInState = (cardId, linkId, updateFn) => {
    setContactLinksByCard(prev => ({
      ...prev,
      [cardId]: prev[cardId]?.map(link =>
        (link._id === linkId || link.id === linkId) ? updateFn(link) : link
      ) || []
    }));
  };

  // Set userId for a specific contact link with validation
  const setUserId = (linkId, cardId, userId, requestingUserId = getCurrentUserId()) => {
    try {
      if (requestingUserId && !isInSignupFlow()) {
        validateContactLinkAccess(linkId, requestingUserId);
      }

      updateLinkInState(cardId, linkId, link => ({ ...link, userId }));
    } catch (error) {
      handleContactLinkError(error);
    }
  };

  // Set cardId for a specific contact link with validation
  const setCardId = (linkId, cardId, newCardId, requestingUserId = getCurrentUserId()) => {
    try {
      if (requestingUserId && !isInSignupFlow()) {
        validateContactLinkAccess(linkId, requestingUserId);
      }

      updateLinkInState(cardId, linkId, link => ({ ...link, cardId: newCardId }));
    } catch (error) {
      handleContactLinkError(error);
    }
  };

  // Set the type of the contact link with validation
  const setLinkType = (linkId, cardId, type, requestingUserId = getCurrentUserId()) => {
    try {
      if (requestingUserId && !isInSignupFlow()) {
        validateContactLinkAccess(linkId, requestingUserId);
      }

      updateLinkInState(cardId, linkId, link => ({ ...link, type }));
    } catch (error) {
      handleContactLinkError(error);
    }
  };

  // Set the URL of the contact link with validation
  const setLinkUrl = (linkId, cardId, linkUrl, requestingUserId = getCurrentUserId()) => {
    try {
      if (requestingUserId && !isInSignupFlow()) {
        validateContactLinkAccess(linkId, requestingUserId);
      }

      updateLinkInState(cardId, linkId, link => ({ ...link, linkUrl }));
    } catch (error) {
      handleContactLinkError(error);
    }
  };

  // Set the title of the contact link with validation
  const setLinkTitle = (linkId, cardId, linkTitle, requestingUserId = getCurrentUserId()) => {
    try {
      if (requestingUserId && !isInSignupFlow()) {
        validateContactLinkAccess(linkId, requestingUserId);
      }

      updateLinkInState(cardId, linkId, link => ({ ...link, linkTitle }));
    } catch (error) {
      handleContactLinkError(error);
    }
  };

  // Set additional details for the contact link with validation
  const setAdditionalDetails = (linkId, cardId, additionalDetails, requestingUserId = getCurrentUserId()) => {
    try {
      if (requestingUserId && !isInSignupFlow()) {
        validateContactLinkAccess(linkId, requestingUserId);
      }

      updateLinkInState(cardId, linkId, link => ({ ...link, additionalDetails }));
    } catch (error) {
      handleContactLinkError(error);
    }
  };

  // Set formatted link for the contact link with validation
  const setFormattedLink = (linkId, cardId, formattedLink, requestingUserId = getCurrentUserId()) => {
    try {
      if (requestingUserId && !isInSignupFlow()) {
        validateContactLinkAccess(linkId, requestingUserId);
      }

      updateLinkInState(cardId, linkId, link => ({ ...link, formattedLink }));
    } catch (error) {
      handleContactLinkError(error);
    }
  };

  // Set highlight status for the contact link with validation
  const setIsHighlighted = (linkId, cardId, isHighlighted, requestingUserId = getCurrentUserId()) => {
    try {
      if (requestingUserId && !isInSignupFlow()) {
        validateContactLinkAccess(linkId, requestingUserId);
      }

      updateLinkInState(cardId, linkId, link => ({ ...link, isHighlighted }));
    } catch (error) {
      handleContactLinkError(error);
    }
  };

  // ✅ FIXED: Save all contact links from context to database
  const saveAllContactLinksToDatabase = async (permanentCardId, userId, sourceCardId = null) => {
    try {
      setContactLinkError(null);

      // ✅ FIXED: Auto-detect source card ID if not provided
      let sourceCardIdToUse = sourceCardId;
      if (!sourceCardIdToUse) {
        if (contactLinksByCard['tempCardId'] && contactLinksByCard['tempCardId'].length > 0) {
          sourceCardIdToUse = 'tempCardId';
        } else {
          // Look for UUID card IDs in the context
          const uuidCardIds = Object.keys(contactLinksByCard).filter(id =>
            id.includes('-') && id.length >= 32 && id.length <= 36
          );
          if (uuidCardIds.length > 0) {
            sourceCardIdToUse = uuidCardIds[0];
          }
        }
      }

      // ✅ FIXED: Look for context links under the detected source card ID
      const contextLinks = contactLinksByCard[sourceCardIdToUse] || [];

      console.log('🔍 ContactLinkContext: Saving context links to database:', {
        permanentCardId,
        sourceCardId: sourceCardIdToUse,
        userId,
        linkCount: contextLinks.length,
        allCardIds: Object.keys(contactLinksByCard),
        contextLinks: contextLinks
      });

      if (contextLinks.length === 0) {
        console.log('🔍 ContactLinkContext: No context links to save');
        return { success: true, saved: 0 };
      }

      const savedLinks = [];
      let saveCount = 0;

      // Save each contact link individually to the permanent card
      for (const link of contextLinks) {
        try {
          const contactLinkData = {
            userId: userId,
            cardId: permanentCardId, // ✅ Use the permanent card ID for saving
            type: link.type,
            details: link.details,
            isHighlighted: link.isHighlighted || false
          };

          console.log('🔍 ContactLinkContext: Saving individual link:', {
            type: link.type,
            hasDetails: !!link.details,
            linkTitle: link.details?.linkTitle,
            permanentCardId
          });

          const response = await createContactLink(contactLinkData);

          if (response.success) {
            savedLinks.push(response.contactLink);
            saveCount++;
            console.log('✅ ContactLinkContext: Link saved successfully:', response.contactLink._id);
          } else {
            console.error('❌ ContactLinkContext: Failed to save link:', response.message);
          }
        } catch (linkError) {
          console.error('❌ ContactLinkContext: Error saving individual link:', linkError);
          // Continue with other links even if one fails
        }
      }

      // Update context: set saved links to permanent card and clear source card
      if (savedLinks.length > 0) {
        setContactLinksByCard(prev => ({
          ...prev,
          [permanentCardId]: savedLinks, // ✅ Store saved links under permanent card ID
          [sourceCardIdToUse]: [] // ✅ Clear source card links
        }));
      }

      console.log('✅ ContactLinkContext: Bulk save completed:', {
        attempted: contextLinks.length,
        saved: saveCount,
        success: saveCount > 0,
        permanentCardId
      });

      return {
        success: saveCount > 0,
        saved: saveCount,
        total: contextLinks.length,
        links: savedLinks
      };

    } catch (error) {
      console.error('❌ ContactLinkContext: Error in bulk save:', error);
      handleContactLinkError(error);
      throw error;
    }
  };

  // ✅ SIMPLIFIED: No more complex transfer logic - just context management
  const transferTemporaryLinks = async (tempCardId, permanentCardId, userId) => {
    try {
      setContactLinkError(null);

      console.log('🔍 ContactLinkContext: Context-only transfer:', {
        tempCardId,
        permanentCardId,
        userId
      });

      // ✅ CONTEXT-ONLY: Just move data from temp card to permanent card in context
      const tempLinks = contactLinksByCard[tempCardId] || [];

      if (tempLinks.length > 0) {
        // Save context links to database and update permanent card
        const saveResult = await saveAllContactLinksToDatabase(permanentCardId, userId, tempCardId);

        if (saveResult.success) {
          // Clear the temporary card context
          setContactLinksByCard(prev => {
            const newState = { ...prev };
            delete newState[tempCardId];
            return newState;
          });

          // Update current card if it's the temp card
          if (currentCardId === tempCardId) {
            setCurrentCard(permanentCardId);
          }

          console.log('✅ ContactLinkContext: Context transfer completed successfully');
          return {
            success: true,
            transferred: saveResult.saved,
            total: saveResult.total,
            links: saveResult.links
          };
        } else {
          throw new Error('Failed to save contact links to database');
        }
      } else {
        console.log('🔍 ContactLinkContext: No temp links to transfer');
        // Still set empty array for the permanent card
        setContactLinksByCard(prev => ({
          ...prev,
          [permanentCardId]: []
        }));

        return {
          success: true,
          transferred: 0,
          total: 0,
          links: []
        };
      }

    } catch (error) {
      console.error('❌ ContactLinkContext: Error in context transfer:', error);
      handleContactLinkError(error);
      throw error;
    }
  };

  // ✅ Enhanced debugging for addContactLink
  const addContactLink = (newLink) => {
    try {
      console.log('🔍 ADD_CONTACT_LINK_START:', {
        newLink,
        currentCardId,
        targetCardId: newLink.cardId || newLink.tempCardId || currentCardId,
        beforeState: contactLinksByCard,
        timestamp: new Date().toISOString()
      });

      setContactLinkError(null);

      // Determine which card this link belongs to
      const targetCardId = newLink.cardId || newLink.tempCardId || currentCardId;

      if (!targetCardId) {
        console.error('🔍 ContactLinkContext: No card ID available for new contact link');
        throw new Error('No card ID available for new contact link');
      }

      console.log('🔍 ContactLinkContext: Target card ID determined:', targetCardId);

      const flowType = detectFlowType(targetCardId, getCurrentUserId());

      if (flowType === 'CONTEXT_ONLY') {
        console.log('🔍 ContactLinkContext: CONTEXT_ONLY - adding to context only');

        const contextLink = {
          ...newLink,
          _id: newLink._id || `temp_${Date.now()}_${Math.random()}`,
          id: newLink.id || `temp_${Date.now()}_${Math.random()}`,
          cardId: targetCardId,
          // ✅ FRONTEND-ONLY FIX: Ensure isVisible defaults to true for new links
          isVisible: newLink.isVisible !== undefined ? newLink.isVisible : true
        };

        setContactLinksByCard(prev => {
          const existingLinks = prev[targetCardId] || [];
          const safeExistingLinks = Array.isArray(existingLinks) ? existingLinks : [];
          const updatedLinks = [...safeExistingLinks, contextLink];

          const newState = {
            ...prev,
            [targetCardId]: updatedLinks
          };

          console.log('🔍 ADD_CONTACT_LINK_STATE_UPDATE:', {
            targetCardId,
            previousCount: safeExistingLinks.length,
            newCount: updatedLinks.length,
            addedLinkVisible: contextLink.isVisible,
            previousState: prev,
            newState: newState,
            addedLink: contextLink,
            timestamp: new Date().toISOString()
          });

          return newState;
        });
      } else {
        // Normal flow - add to database context and invalidate cache for refetch
        const existingLinks = contactLinksByCard[targetCardId] || [];
        const linkExists = existingLinks.some(link =>
          (link._id && link._id === newLink._id) ||
          (link.id && link.id === newLink.id)
        );

        if (linkExists) {
          console.log('🔍 ContactLinkContext: Contact link already exists, not adding duplicate');
          return;
        }

        // ✅ FRONTEND-ONLY FIX: Ensure isVisible is set for database links too
        const linkWithVisibility = {
          ...newLink,
          isVisible: newLink.isVisible !== undefined ? newLink.isVisible : true
        };

        setContactLinksByCard(prev => {
          const updatedLinks = [...(prev[targetCardId] || []), linkWithVisibility];
          console.log('🔍 ContactLinkContext: Adding link to database context:', {
            targetCardId,
            previousCount: prev[targetCardId]?.length || 0,
            newCount: updatedLinks.length,
            addedLinkVisible: linkWithVisibility.isVisible
          });

          return {
            ...prev,
            [targetCardId]: updatedLinks
          };
        });

        // ✅ CRITICAL: Force refetch to get the latest data from database
        console.log('🔍 ContactLinkContext: Invalidating cache for card after adding link:', targetCardId);
        setTimeout(() => {
          forceRefetchCard(targetCardId);
        }, 1000); // Small delay to allow database to sync
      }

      // Set current card if needed
      if (targetCardId === currentCardId || !currentCardId) {
        console.log('🔍 SETTING_CURRENT_CARD:', { from: currentCardId, to: targetCardId });
        setCurrentCard(targetCardId);
      }

      console.log('🔍 ADD_CONTACT_LINK_COMPLETED:', { targetCardId });
      setTimeout(() => {
        const afterState = contactLinksByCard[targetCardId] || [];
        console.log('🔍 ADD_CONTACT_LINK_VERIFICATION:', {
          targetCardId,
          afterStateLength: afterState.length,
          afterState: afterState,
          allCardsAfter: contactLinksByCard,
          timestamp: new Date().toISOString()
        });
      }, 100);

    } catch (error) {
      console.error('🔍 ADD_CONTACT_LINK_ERROR:', error);
      handleContactLinkError(error);
    }
  };

  // Update contact link ID with validation
  const updateContactLinkId = (linkId, cardId, newId, requestingUserId = getCurrentUserId()) => {
    try {
      if (requestingUserId && !isInSignupFlow()) {
        validateContactLinkAccess(linkId, requestingUserId);
      }

      updateLinkInState(cardId, linkId, link => ({ ...link, id: newId, _id: newId }));
    } catch (error) {
      handleContactLinkError(error);
    }
  };

  // ✅ CONTEXT-ONLY: Update contact link with smart sync logic and cache invalidation
  const updateContactLink = async (linkId, cardId, updatedLinkDetails, syncWithServer = false) => {
    try {
      setContactLinkError(null);

      console.log('🔍 ContactLinkContext: updateContactLink called with:', {
        linkId,
        cardId,
        updatedLinkDetails,
        syncWithServer,
        currentUserId: getCurrentUserId(),
        isVisible: updatedLinkDetails?.isVisible
      });

      const flowType = detectFlowType(cardId, getCurrentUserId());
      console.log('🔍 ContactLinkContext: Flow type detected:', flowType);

      // Find the link to update first
      const linkToUpdate = (contactLinksByCard[cardId] || []).find(link =>
        link._id === linkId || link.id === linkId
      );

      if (linkToUpdate) {
        // ✅ FRONTEND-ONLY FIX: Handle isVisible specially
        let localUpdates = { ...updatedLinkDetails };

        // If isVisible is being updated, store it in details.additionalDetails
        if ('isVisible' in updatedLinkDetails) {
          const currentDetails = linkToUpdate.details || {};
          const currentAdditionalDetails = currentDetails.additionalDetails || {};

          localUpdates = {
            ...localUpdates,
            details: {
              ...currentDetails,
              additionalDetails: {
                ...currentAdditionalDetails,
                isVisible: updatedLinkDetails.isVisible
              }
            }
          };

          // Remove isVisible from root level for local state
          delete localUpdates.isVisible;

          console.log('🔍 ContactLinkContext: Storing isVisible in additionalDetails:', {
            isVisible: updatedLinkDetails.isVisible,
            updatedAdditionalDetails: localUpdates.details.additionalDetails
          });
        }

        // Update local state immediately
        setContactLinksByCard(prev => ({
          ...prev,
          [cardId]: prev[cardId]?.map(link =>
            (link._id === linkId || link.id === linkId)
              ? {
                ...link,
                ...localUpdates,
                details: { ...link.details, ...localUpdates.details },
                // Keep isVisible at root level for frontend use
                isVisible: updatedLinkDetails.isVisible !== undefined
                  ? updatedLinkDetails.isVisible
                  : link.isVisible
              }
              : link
          ) || []
        }));
      }

      if (flowType === 'CONTEXT_ONLY') {
        console.log('🔍 ContactLinkContext: CONTEXT_ONLY - updating context only');
        return;
      }

      // ✅ NORMAL FLOW: Validate user access and potentially sync with server
      const currentUserId = getCurrentUserId();
      if (currentUserId && linkToUpdate?.userId && linkToUpdate.userId !== currentUserId) {
        throw new Error('You do not have permission to modify this contact link');
      }

      // Optionally sync with server
      if (syncWithServer) {
        try {
          console.log('🔍 ContactLinkContext: Making API call to update contact link');

          const apiUpdateData = {};
          if (updatedLinkDetails.type !== undefined) {
            apiUpdateData.type = updatedLinkDetails.type;
          }
          if (updatedLinkDetails.isHighlighted !== undefined) {
            apiUpdateData.isHighlighted = updatedLinkDetails.isHighlighted;
          }

          // ✅ FRONTEND-ONLY FIX: Handle isVisible in details
          if (updatedLinkDetails.details || 'isVisible' in updatedLinkDetails) {
            const currentDetails = linkToUpdate.details || {};
            const currentAdditionalDetails = currentDetails.additionalDetails || {};

            apiUpdateData.details = {
              ...currentDetails,
              ...updatedLinkDetails.details,
              additionalDetails: {
                ...currentAdditionalDetails,
                ...updatedLinkDetails.details?.additionalDetails,
                // Store visibility in additionalDetails
                ...(('isVisible' in updatedLinkDetails) && {
                  isVisible: updatedLinkDetails.isVisible
                })
              }
            };
          }

          console.log('🔍 ContactLinkContext: API update data:', apiUpdateData);

          const result = await handleApiCall(
            () => apiUpdateContactLink(linkId, apiUpdateData),
            {
              source: 'contact-link-update',
              retryable: true,
              autoDismiss: true
            }
          );
          console.log('🔍 ContactLinkContext: API call successful:', result);

          // ✅ CRITICAL: Force refetch after successful server update
          console.log('🔍 ContactLinkContext: Force refetching after successful update');
          setTimeout(() => {
            forceRefetchCard(cardId);
          }, 500);

        } catch (error) {
          console.error('🔍 ContactLinkContext: API call failed, reverting changes:', error);
          // Revert local changes if server update fails
          if (linkToUpdate) {
            setContactLinksByCard(prev => ({
              ...prev,
              [cardId]: prev[cardId]?.map(link =>
                (link._id === linkId || link.id === linkId) ? linkToUpdate : link
              ) || []
            }));
          }
          handleContactLinkError(error);
          throw error;
        }
      }
    } catch (error) {
      handleContactLinkError(error);
      throw error;
    }
  };

  // ✅ CONTEXT-ONLY: Delete contact link with smart sync logic
  const deleteContactLink = async (linkId, cardId, userId = getCurrentUserId(), syncWithServer = false) => {
    try {
      setContactLinkError(null);

      console.log('🔍 ContactLinkContext: deleteContactLink called with:', {
        linkId,
        cardId,
        userId,
        syncWithServer,
        currentLinks: contactLinksByCard[cardId]?.length || 0,
        allCardIds: Object.keys(contactLinksByCard),
        currentCardId
      });

      const flowType = detectFlowType(cardId, userId);
      console.log('🔍 ContactLinkContext: Flow type detected:', flowType);

      // Find the link to delete (check both _id and id)
      const link = findLinkById(linkId, cardId);
      if (!link) {
        console.error('🔍 ContactLinkContext: Contact link not found:', linkId);
        throw new Error('Contact link not found');
      }

      console.log('🔍 ContactLinkContext: Found link to delete:', {
        linkId: link._id || link.id,
        type: link.type,
        cardId: link.cardId,
        userId: link.userId
      });

      if (flowType === 'CONTEXT_ONLY') {
        // ✅ SIGNUP FLOW: Only remove from context
        console.log('🔍 ContactLinkContext: CONTEXT_ONLY - removing from context only');
      } else {
        // ✅ NORMAL FLOW: Validate user access
        if (userId && link.userId && link.userId !== userId) {
          throw new Error('You do not have permission to delete this contact link');
        }
      }

      // Store original state for potential rollback
      const originalLinks = contactLinksByCard[cardId] || [];

      // Remove from local state - Proper filtering logic
      const updatedLinks = originalLinks.filter(link => {
        const isTargetLink = (link._id === linkId) || (link.id === linkId);
        console.log('🔍 ContactLinkContext: Checking link for removal:', {
          linkId: link._id || link.id,
          targetId: linkId,
          isTarget: isTargetLink,
          linkType: link.type
        });
        return !isTargetLink; // Keep links that are NOT the target
      });

      console.log('🔍 ContactLinkContext: Updating state:', {
        originalCount: originalLinks.length,
        updatedCount: updatedLinks.length,
        removed: originalLinks.length - updatedLinks.length,
        cardId
      });

      // Update the state immediately
      setContactLinksByCard(prev => {
        const newState = {
          ...prev,
          [cardId]: updatedLinks
        };
        console.log('🔍 ContactLinkContext: New state after deletion:', {
          cardId,
          newCount: updatedLinks.length,
          allCards: Object.keys(newState)
        });
        return newState;
      });

      // Optionally sync with server (only for normal flow)
      if (syncWithServer && flowType === 'DATABASE_SYNC') {
        try {
          console.log('🔍 ContactLinkContext: Making API call to delete contact link');
          const result = await handleApiCall(
            () => apiDeleteContactLink(linkId),
            {
              source: 'contact-link-delete',
              retryable: true,
              autoDismiss: false
            }
          );
          console.log('🔍 ContactLinkContext: API call successful:', result);
        } catch (error) {
          console.error('🔍 ContactLinkContext: API deletion failed, reverting changes:', error);
          // Revert local changes if server deletion fails
          setContactLinksByCard(prev => ({
            ...prev,
            [cardId]: originalLinks
          }));
          handleContactLinkError(error);
          throw error;
        }
      }

      console.log('🔍 ContactLinkContext: Contact link deletion completed successfully');
    } catch (error) {
      console.error('🔍 ContactLinkContext: Error in deleteContactLink:', error);
      handleContactLinkError(error);
      throw error;
    }
  };

  // Clear contact link error
  const clearContactLinkError = () => {
    setContactLinkError(null);
  };

  // Enhanced clear cache method
  const clearFetchedCards = () => {
    setFetchedCards(new Set());
    console.log('🔍 ContactLinkContext: Cleared fetched cards cache');
  };

  // Clear all context data (useful for logout or switching users)
  const clearAllContactLinksData = () => {
    setContactLinksByCard({});
    setCurrentCardId(null);
    setFetchedCards(new Set());
    setContactLinkError(null);
    console.log('🔍 ContactLinkContext: Cleared all contact links data');
  };

  // Validate URL format
  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Sanitize link data
  const sanitizeLinkData = (linkData) => {
    return {
      ...linkData,
      linkUrl: linkData.linkUrl?.trim(),
      linkTitle: linkData.linkTitle?.trim(),
      additionalDetails: linkData.additionalDetails || {},
    };
  };

  // ✅ Add this to check state at any time
  const debugCurrentState = () => {
    console.log('🔍 CURRENT_CONTEXT_STATE:', {
      currentCardId,
      contactLinksByCard,
      contactLinksLength: contactLinks.length,
      contactLinks,
      allCardIds: Object.keys(contactLinksByCard),
      timestamp: new Date().toISOString()
    });
  };

  // ✅ Initialize currentCardId during signup flow
  useEffect(() => {
    const signupInProgress = localStorage.getItem('signupInProgress') === 'true';
    const isAuthenticated = authState?.isAuthenticated;

    // If we're in signup flow and currentCardId is not set, set it to tempCardId
    if (signupInProgress && !isAuthenticated && !currentCardId) {
      console.log('🔍 ContactLinkContext: Initializing currentCardId to tempCardId during signup');
      setCurrentCardId('tempCardId');
    }
  }, [authState?.isAuthenticated, currentCardId]);

  // ✅ Add state monitoring useEffect
  useEffect(() => {
    console.log('🔍 CONTACT_LINKS_BY_CARD_CHANGED:', {
      cardCount: Object.keys(contactLinksByCard).length,
      cards: Object.keys(contactLinksByCard),
      fullState: contactLinksByCard,
      currentCardId,
      timestamp: new Date().toISOString()
    });
  }, [contactLinksByCard, currentCardId]);

  return (
    <ContactLinkContext.Provider
      value={{
        contactLinks,
        contactLinksByCard,
        contactLinkError,
        contactLinkState,
        contactLinkUUID,
        setContactLinksForCard,
        fetchContactLinksForCard,
        forceRefetchCard, // ✅ NEW: Export force refetch method
        invalidateAllCache, // ✅ NEW: Export cache invalidation method
        validateContactLinkAccess,
        isContactLinkOwner,
        getUserContactLinks,
        setUserId,
        setCardId,
        setLinkType,
        setLinkUrl,
        setLinkTitle,
        setAdditionalDetails,
        setFormattedLink,
        setIsHighlighted,
        addContactLink,
        updateContactLinkId,
        updateContactLink,
        deleteContactLink,
        clearContactLinkError,
        clearFetchedCards,
        clearAllContactLinksData,
        validateUrl,
        sanitizeLinkData,
        transferTemporaryLinks, // ✅ SIMPLIFIED: Context-only transfer
        saveAllContactLinksToDatabase, // ✅ FIXED: Save context to database
        setCurrentCard,
        currentCardId,
        detectFlowType,
        isInSignupFlow, // ✅ NEW: Helper to check signup flow
        debugCurrentState, // ✅ Add this for manual debugging
      }}
    >
      {children}
    </ContactLinkContext.Provider>
  );
}

export function useContactLinkContext() {
  const context = useContext(ContactLinkContext);
  if (!context) {
    throw new Error('useContactLinkContext must be used within a ContactLinkProvider');
  }
  return context;
}